import type {RegistrationSelectItem, ValidationRegistrar} from 'modules/VendorRegister/types';

import {yupResolver} from '@hookform/resolvers/yup';
import {toast} from '@Toast';
import FormInput from 'modules/VendorRegister/components/common/FormInput/FormInput';
import {useCallback, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useAppDispatch, useAppSelector} from 'store';
import {
  getDeliveryTypes,
  getSoftwareTypes,
  sendPostalInfo,
  sendVendorInfo
} from 'store/slices/vendor-registration/thunk';
import {p2e} from 'utils/Helper';

import SelectField from './components/SelectField/SelectField';
import {VENDOR_INFO_SCHEMA} from './constants';

type VendorInfoForm = {
  name: string;
  postalCode: string;
  deliveryType: string;
  softwareType: string;
  address: string;
};

type VendorInfoProps = {
  registerValidation: ValidationRegistrar;
  vendorName?: string;
};

const VendorInfo: React.FC<VendorInfoProps> = ({registerValidation, vendorName = ''}) => {
  const dispatch = useAppDispatch();
  const {softwareTypes, deliveryTypes} = useAppSelector((state) => state.vendorRegistration);
  const {control, handleSubmit, watch, setValue} = useForm<VendorInfoForm>({
    resolver: yupResolver(VENDOR_INFO_SCHEMA),
    defaultValues: {
      name: vendorName,
      postalCode: '',
      deliveryType: '',
      softwareType: '',
      address: ''
    }
  });
  const postalCode = watch('postalCode');
  const postalRequestRef = useRef(0);

  const submitVendorInfo = useCallback(
    async (data: VendorInfoForm) => {
      try {
        const response = await dispatch(
          sendVendorInfo({
            address: data.address,
            name: data.name,
            postalCode: p2e(data.postalCode),
            softwareType: data.softwareType,
            deliveryType: data.deliveryType
          })
        ).unwrap();

        if (response.isSuccess) {
          return true;
        }

        toast(response.message || 'خطا در ذخیره سازی اطلاعات');
      } catch (error) {
        toast(
          typeof error === 'object' &&
            error !== null &&
            'message' in error &&
            typeof error.message === 'string'
            ? error.message
            : 'خطا در ذخیره سازی اطلاعات'
        );
      }

      return false;
    },
    [dispatch]
  );

  const validateStep = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      void handleSubmit(
        async (data) => {
          resolve(await submitVendorInfo(data));
        },
        () => resolve(false)
      )();
    });
  }, [handleSubmit, submitVendorInfo]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  useEffect(() => {
    void dispatch(getSoftwareTypes(undefined));
    void dispatch(getDeliveryTypes(undefined));
  }, [dispatch]);

  useEffect(() => {
    if (vendorName) {
      setValue('name', vendorName);
    }
  }, [setValue, vendorName]);

  useEffect(() => {
    const requestId = ++postalRequestRef.current;

    if (postalCode.length !== 10) {
      setValue('address', '');
      return;
    }

    const normalizedPostalCode = p2e(postalCode);
    let cancelled = false;

    void dispatch(sendPostalInfo({postalCode: normalizedPostalCode}))
      .unwrap()
      .then((response) => {
        if (cancelled || requestId !== postalRequestRef.current) {
          return;
        }

        if (response.isSuccess) {
          setValue(
            'address',
            `${response.data.province} - ${response.data.city} - ${response.data.region} - ${response.data.address}`
          );
          return;
        }

        setValue('address', '');
        toast(response.message || 'خطا در دریافت کدپستی');
      })
      .catch((error) => {
        if (!cancelled && requestId === postalRequestRef.current) {
          setValue('address', '');
          toast(error.message || 'خطا در دریافت کدپستی');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, postalCode, setValue]);

  return (
    <div className="flex flex-col gap-2.5 px-5 py-2.5">
      <FormInput
        name="name"
        control={control}
        required
        label="نام فروشگاه"
        placeholder="نام فروشگاه خود وارد کنید"
        disabled
        readOnly
      />
      <FormInput
        name="postalCode"
        control={control}
        required
        label="کد پستی"
        type="tel"
        inputMode="numeric"
        maxLength={10}
        placeholder="کد پستی خود وارد کنید"
      />
      <FormInput name="address" control={control} required label="آدرس فروشگاه" disabled />
      <SelectField
        name="softwareType"
        control={control}
        label="نرم افزار فروشگاهی"
        items={softwareTypes as RegistrationSelectItem[]}
        placeholder="نرم افزار فروشگاهی را انتخاب کنید"
        required
      />
      <SelectField
        name="deliveryType"
        control={control}
        label="نحوه ارسال"
        items={deliveryTypes as RegistrationSelectItem[]}
        placeholder="نحوه ارسال را انتخاب کنید"
        required
      />
    </div>
  );
};

export default VendorInfo;
